import PlaylistTree from './PlaylistTree'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'
import { useEffect, useRef, useState } from 'react'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg'
import { ReactComponent as CopyIcon } from 'assets/icons/copy.svg'
import { ReactComponent as PasteIcon } from 'assets/icons/paste.svg'
import { ReactComponent as UpdateIcon } from 'assets/icons/update.svg'

import isAncestor from '../../utils/isAncestor'
import renderSortedPlaylistData from '../../utils/renderSortedPlaylistData'
import pastePlaylistData from '../../utils/pastePlaylistData'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectUserPreference,
  setUserPreference,
} from '../../redux/userPreferences'
import CreateModal from '../GeneralModal/CreateModal'
import useIsSubscribed from '../../utils/hooks/useIsSubscribed'
const PlaylistEditor = ({
  values,
  onChange: _onChange,
  onPublishChangesClick,
  onEditTrack = () => {},
  isOutOfDate,
  mode = 'library',
  isLoading,
}) => {
  const dispatch = useDispatch()
  const ref = useRef(null)
  const isPortfolioMode = mode === 'portfolio'

  const [selectedItems, setSelectedItems] = useState([])
  const [copyBuffer, setCopyBuffer] = useState([])
  const [undoStates, setUndoStates] = useState([])
  const [redoStates, setRedoStates] = useState([])
  const [createModalIsOpen, setCreateModalIsOpen] = useState(false)
  const [dontShowPublishWarningAgain, setDontShowPublishWarningAgain] =
    useState(false)
  const [openFolders, setOpenFolders] = useState(isPortfolioMode ? ['p1'] : [])
  const hidePlaylistPublishWarning = useSelector((state) =>
    selectUserPreference(state, 'hidePlaylistPublishWarning'),
  )
  const isSubscribed = useIsSubscribed()
  const tracksCopied = copyBuffer.filter(
    (item) => item.id.slice(0, 1) === 't',
  ).length
  const groupsCopied = copyBuffer.filter(
    (item) => item.id.slice(0, 1) === 'g',
  ).length
  const hasItems = values?.data?.length > 0
  const hasPendingChanges = values?.data?.some(
    (item) => item?.data?.pendingChanges,
  )
  const disableNewPlaylist =
    !isSubscribed && values?.data?.some((item) => item?.id?.slice(0, 1) === 'p')
  useEffect(() => {
    const clickEvent = (evt) => {
      if (!ref.current.contains(evt.target)) {
        setSelectedItems([])
      }
    }

    const keydownEvent = (evt) => {
      const activeElement = document.activeElement
      const notInput =
        activeElement.tagName !== 'INPUT' &&
        activeElement.tagName !== 'TEXTAREA' &&
        document.activeElement.contentEditable !== 'true'
      if (selectedItems.length > 0 && notInput) {
        if ((evt.metaKey || evt.ctrlKey) && evt.key === 'c') {
          handleCopy()
        } else if (
          (evt.metaKey || evt.ctrlKey) &&
          evt.keyCode === 86 &&
          selectedItems.length > 0
        ) {
          handlePaste()
        } else if (evt.key === 'Delete' || evt.key === 'Backspace') {
          handleDelete()
        }
      }

      if (notInput) {
        if ((evt.metaKey || evt.ctrlKey) && evt.key.toLowerCase() === 'z') {
          if (evt.shiftKey) {
            handleRedo()
          } else {
            handleUndo()
          }
        } else if (
          (evt.metaKey || evt.ctrlKey) &&
          evt.key.toLowerCase() === 'y'
        ) {
          handleRedo()
        }
      }
    }

    if (ref.current) {
      document.addEventListener('click', clickEvent)
      document.addEventListener('keydown', keydownEvent)
    }

    return () => {
      document.removeEventListener('click', clickEvent)
      document.removeEventListener('keydown', keydownEvent)
    }
  }, [
    ref,
    JSON.stringify(selectedItems),
    JSON.stringify(values),
    JSON.stringify(copyBuffer),
    JSON.stringify(undoStates),
    JSON.stringify(redoStates),
  ])

  function onChange(changeSet, flags) {
    _onChange({ ...changeSet, modified: Date.now() }, flags)
    setUndoStates([...undoStates, cloneDeep(values)])
    setRedoStates([])
  }

  function handleNewPlaylist(name) {
    if (name) {
      const nextId = values.playlistId + 1
      const id = `p${nextId}`
      onChange({
        ...values,
        playlistId: nextId,
        data: [
          ...(values.data || []),
          {
            id,
            parent: 0,
            droppable: true,
            text: name,
          },
        ],
      })
      setOpenFolders([...openFolders, id])
    }
  }

  function handleItemClick(evt, item) {
    const isSelected = !!selectedItems.find((search) => search === item.id)
    const isMouseDown = evt.type === 'mousedown'
    const isClick = evt.type === 'click'
    const hasMod = evt.ctrlKey || evt.metaKey
    const hasShift = evt.shiftKey
    if (hasShift) {
      if (isMouseDown) {
        const items = renderSortedPlaylistData(values.data)

        const lastSelectedItem = [...selectedItems].pop()
        const itemIndex = items.findIndex((search) => item.id === search.id)
        const lastIndex = items.findIndex(
          (search) => search.id === lastSelectedItem,
        )
        if (itemIndex < lastIndex) {
          setSelectedItems([
            ...selectedItems,
            ...items
              .slice(itemIndex, lastIndex)
              .filter(
                (item) =>
                  item.id.slice(0, 1) !== 'p' &&
                  !selectedItems.find((search) => search === item.id),
              )
              .map((item) => item.id),
          ])
        } else if (itemIndex > lastIndex) {
          setSelectedItems([
            ...selectedItems,
            ...items
              .slice(lastIndex, itemIndex + 1)
              .filter(
                (item) =>
                  item.id.slice(0, 1) !== 'p' &&
                  !selectedItems.find((search) => search === item.id),
              )
              .map((item) => item.id),
          ])
        }
      }
    } else {
      if (isMouseDown) {
        if (hasMod) {
          const newItems = selectedItems.filter((search) => search !== item.id)
          if (isSelected) {
            setSelectedItems(newItems)
          } else {
            setSelectedItems([...newItems, item.id])
          }
        } else if (!isSelected) {
          setSelectedItems([item.id])
        }
      }
      if (isClick && !hasMod) {
        setSelectedItems([item.id])
      }
    }
  }

  function handleChange(changeSet, flags) {
    onChange(changeSet, flags)
    if (flags?.clearSelection) {
      setSelectedItems([])
    }
  }

  function handleCopy() {
    const items = []
    const topLevelItems = selectedItems.filter(
      (item) =>
        !selectedItems.find((search) => isAncestor(values.data, search, item)),
    )
    const orderedTree = renderSortedPlaylistData(values.data)
    orderedTree.forEach((item) => {
      if (!!topLevelItems.find((search) => search === item.id)) {
        items.push(item)
      }
    })
    const fullItems = items.reduce((acc, cur) => {
      if (cur.id.slice(0, 1) === 'g') {
        const startIndex = orderedTree.findIndex(
          (search) => search.id === cur.id,
        )
        const endIndex = orderedTree.findIndex(
          (search, index) =>
            index > startIndex && !isAncestor(values.data, cur.id, search.id),
        )
        acc.push(cur, ...orderedTree.slice(startIndex + 1, endIndex))
      } else {
        acc.push(cur)
      }
      return acc
    }, [])

    setCopyBuffer(fullItems)
  }

  function handleDelete() {
    const currentTree = values.data
    const newTree = currentTree.filter((item) => {
      for (let i of selectedItems) {
        if (item.id === i || isAncestor(currentTree, i, item.id)) {
          return false
        }
      }
      return true
    })
    onChange({
      ...values,
      data: newTree,
    })
    setSelectedItems([])
  }

  function handlePaste() {
    const itemId = selectedItems[0]
    const itemIndex = values.data.findIndex((search) => search.id === itemId)
    const item = values.data[itemIndex]
    onChange(pastePlaylistData(values, copyBuffer, item.parent, itemIndex + 1))
  }

  function handleUndo() {
    if (undoStates.length > 0) {
      const newUndoState = [...undoStates]
      _onChange({ ...newUndoState.pop(), modified: Date.now() })
      setUndoStates(newUndoState)
      setRedoStates([...redoStates, cloneDeep(values)])
    }
  }

  function handleRedo() {
    if (redoStates.length > 0) {
      const newRedoState = [...redoStates]
      _onChange({ ...newRedoState.pop(), modified: Date.now() })
      setRedoStates(newRedoState)
      setUndoStates([...undoStates, cloneDeep(values)])
    }
  }

  function handleItemDoubleClick(item) {
    if (item.id.slice(0, 1) === 't' && onEditTrack) {
      onEditTrack(item.data.trackId)
    }
  }

  function handleDismissPublishWarning() {
    dispatch(
      setUserPreference({
        key: 'hidePlaylistPublishWarning',
        value: 1,
      }),
    )
    if (dontShowPublishWarningAgain) {
      window.localStorage.setItem('hidePlaylistPublishWarning', 1)
    }
  }

  return (
    <div className="playlist-editor flex flex-col flex-1 h-full" ref={ref}>
      {!isPortfolioMode && (
        <div className="p-4 min-h-[72px]">
          {hasPendingChanges && !hidePlaylistPublishWarning && (
            <div className="border border-deep rounded bg-gray-dark p-4 flex items-center relative">
              <div className="mr-4">
                <UpdateIcon fill="#d63b5c" height="44" width="44" />
              </div>
              <div className="flex-1">
                <h3>Pending Changes</h3>
                <div className="mt-2">
                  <div>
                    Playlists need to have their changes published using the
                    publish button.
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-1 flex items-center mt-2">
                    <input
                      name="dontShow"
                      id="dontShow"
                      type="checkbox"
                      value={dontShowPublishWarningAgain}
                      onChange={(evt) =>
                        setDontShowPublishWarningAgain(evt.target.checked)
                      }
                    />
                    <label htmlFor="dontShow" className="ml-1 mb-0">
                      Don't show this message again
                    </label>
                  </div>
                  <div className="mt-2">
                    <button
                      className="button-sm bg-gray-light"
                      onClick={handleDismissPublishWarning}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>

              <button
                className="button icon absolute top-[-4px] right-[-4px]"
                onClick={handleDismissPublishWarning}
              >
                <CloseIcon fill="#ccc" height="24" width="24" />
              </button>
            </div>
          )}
        </div>
      )}

      <div
        className={clsx('mb-5 px-6 flex flex-row relative h-[40px]', {
          'pt-4': isPortfolioMode,
        })}
      >
        {selectedItems.length === 0 && !isPortfolioMode ? (
          <button
            type="button"
            className="button bg-pink hover:bg-pink-off"
            onClick={() => setCreateModalIsOpen(true)}
            disabled={disableNewPlaylist}
            {...(disableNewPlaylist
              ? {
                  'data-tooltip':
                    'Free accounts are limited to one playlist / player.',
                }
              : {})}
          >
            New Playlist
          </button>
        ) : (
          <div className="flex items-center flex-1">
            {selectedItems.length > 0 && (
              <>
                <button
                  className="button icon bg-white-purple hover:bg-white dark:bg-gray-light dark:hover:bg-gray-dark"
                  type="button"
                  key={'unique-button'}
                  onClick={() => setSelectedItems([])}
                >
                  <CloseIcon fill="#eee" height="24" width="24" />
                </button>
                <div className="flex items-center tracking-wide leading-4 ml-2 ">
                  {selectedItems.length} selected
                </div>

                <button
                  className="button icon bg-white-purple hover:bg-white dark:bg-gray-light dark:hover:bg-gray-dark ml-8"
                  onClick={handleCopy}
                  data-tooltip={`Copy Item${
                    selectedItems.length > 1 ? 's' : ''
                  } (Ctrl/Cmd + C)`}
                >
                  <CopyIcon fill="#eee" height="22" width="22" />
                </button>
                {selectedItems.length === 1 && (
                  <button
                    className={clsx(
                      'button icon bg-white-purple hover:bg-white dark:bg-gray-light dark:hover:bg-gray-dark ml-2',
                      {
                        'opacity-40 pointer-events-none':
                          copyBuffer.length === 0,
                      },
                    )}
                    onClick={handlePaste}
                    data-tooltip={`Paste Item${
                      copyBuffer.length > 1 ? 's' : ''
                    }  (Ctrl/Cmd + V)`}
                  >
                    <PasteIcon fill="#eee" height="22" width="22" />
                  </button>
                )}
                <button
                  className="button icon bg-white-purple hover:bg-white dark:bg-gray-light dark:hover:bg-gray-dark ml-2"
                  data-tooltip={`Delete Item${
                    selectedItems.length > 1 ? 's' : ''
                  }`}
                  onClick={handleDelete}
                >
                  <DeleteIcon fill="#eee" height="18" width="18" />
                </button>
              </>
            )}
            <>
              <div className="flex-1"></div>
              {copyBuffer.length > 0 && !isPortfolioMode && (
                <div className="bg-gray rounded border border-solid border-gray-dark pr-4 items-center h-[34px] flex ">
                  <button
                    className="button bg-gray mx-2 w-[34px] h-full no-padding flex items-center justify-center"
                    type="button"
                    key={'unique-button'}
                    onClick={() => setCopyBuffer([])}
                    data-tooltip="Clear Copied Items"
                  >
                    <CloseIcon fill="#eee" height="24" width="24" />
                  </button>
                  Copied&nbsp;
                  {groupsCopied > 0 && <span>{groupsCopied} Folders</span>}{' '}
                  {groupsCopied > 0 && tracksCopied > 0 && (
                    <span>&nbsp;and&nbsp;</span>
                  )}{' '}
                  {tracksCopied > 0 && <span>{tracksCopied} Tracks</span>}.
                </div>
              )}
            </>
          </div>
        )}
        {hasItems && isLoading && (
          <div className="absolute w-[calc(100%-48px)] justify-center items-center flex">
            <div className="flex row px-8 py-2 border border-gray-light drop-shadow-dark bg-gray rounded items-center text-white-dark">
              <div className="">
                <LoadingSpinner size="small" />
              </div>
              <div className="ml-2">Saving...</div>
            </div>
          </div>
        )}
      </div>
      {hasItems ? (
        <PlaylistTree
          values={values}
          onChange={handleChange}
          isLoading={isLoading}
          isOutOfDate={isOutOfDate}
          onPublishChangesClick={onPublishChangesClick}
          onItemClick={handleItemClick}
          onItemDoubleClick={handleItemDoubleClick}
          selectedItems={selectedItems}
          copyBuffer={copyBuffer}
          hidePlaylistOptions={isPortfolioMode}
          openFolders={openFolders}
        />
      ) : (
        <div className="flex items-center justify-center">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="mt-6 p-6 mx-8 bg-gray border border-gray-dark rounded">
              <h4 className="mb-4">No Playlists</h4>
              <p>
                Get started by creating your first playlist above. You can then
                create groups or just start dragging tracks in!
              </p>
            </div>
          )}
        </div>
      )}
      <CreateModal
        isOpen={createModalIsOpen}
        onRequestClose={() => setCreateModalIsOpen(false)}
        title="Create Playlist"
        label="Enter Playlist Name"
        onSubmit={handleNewPlaylist}
      />
    </div>
  )
}

export default PlaylistEditor
